import { Component, OnInit, ViewChild } from '@angular/core';
import { MatOptgroup } from '@angular/material/autocomplete';
import {
  MatSelect,
  MatSelectChange,
  MatSelectModule,
} from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ReferenceCountryModel } from '../../models/reference-country.model';
import { Router } from '@angular/router';
import { UserCountrySelectedService } from '../../services/user-country-selected.service';
import { UserSignatureSelectedService } from '../../services/user-signature-selected.service';
import { ReferenceAppServiceModel } from '../../models/reference-app-service.model';
import { ReferenceSignaturesService } from '../../services/reference-signatures.service';
import {
  MatButtonToggleChange,
  MatButtonToggleModule,
} from '@angular/material/button-toggle';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  saxBook1Outline,
  saxGlobalOutline,
  saxProfile2userOutline,
  saxProfileTickOutline,
} from '@ng-icons/iconsax/outline';
import { DashboardViewEnum } from '../../enum/dashboard-view.enum';
import { MatIconModule } from '@angular/material/icon';
import { UserDashboardViewSelectedService } from '../../services/user-dashboard-view-selected.service';
import { ReferenceSignatureModel } from '../../models/reference-signature.model';
import { ReferenceZoneModel } from '../../models/reference-zone.model';
import { ReferenceCountriesService } from '../../services/reference-countries.service';
import { AuthService } from '../../../views/authentification/services/auth.service';
import { UserManagementService } from '../../../views/user-management/services/user-management.service';
import { AdUserModel } from '../../../views/user-management/models/adUser.model';

@Component({
  selector: 'app-navigation-bar',
  standalone: true,
  imports: [
    CommonModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatButtonToggleModule,
    MatOptgroup,
    NgIconComponent,
    MatIconModule,
  ],
  providers: [
    provideIcons({
      saxBook1Outline,
      saxGlobalOutline,
      saxProfileTickOutline,
      saxProfile2userOutline,
    }),
  ],
  templateUrl: './navigation-bar.component.html',
  styleUrl: './navigation-bar.component.scss',
})
export class NavigationBarComponent implements OnInit {
  zoneList: ReferenceZoneModel[] = [];
  countryList: ReferenceCountryModel[] = [];
  referenceSignatureList: ReferenceSignatureModel[] = [];

  userSignatureInfoSelected?: string;

  signatureSelected?: ReferenceSignatureModel;
  appServiceSelected?: ReferenceAppServiceModel;

  countryCodeSelected?: string;
  countrySelected?: ReferenceCountryModel;

  currentSignatureImg?: string;
  currentSignatureImgSrcset?: string;

  @ViewChild('selectSignature') selectBrand: MatSelect | undefined;
  @ViewChild('selectCountry') selectCountry: MatSelect | undefined;

  userInfo?: AdUserModel;

  currentDashboardViewSelected = new FormControl(
    DashboardViewEnum.DashboardViewInternationalCatalogs
  );

  constructor(
    private _authService: AuthService,
    private _router: Router,
    private userCountrySelectedService: UserCountrySelectedService,
    private userSignatureSelectedService: UserSignatureSelectedService,
    private userDashboardViewSelectedService: UserDashboardViewSelectedService,
    private referenceSignatureService: ReferenceSignaturesService,
    private referenceCountriesService: ReferenceCountriesService,
    private userManagementService: UserManagementService
  ) {}

  async ngOnInit(): Promise<void> {
    this.userManagementService
      .getCurrentUserDetails()
      .subscribe((userInfo: AdUserModel | undefined) => {
        this.userInfo = userInfo;

        this.referenceSignatureService
          .getList()
          .subscribe(
            (referenceSignatureList: Array<ReferenceSignatureModel>) => {
              this.referenceSignatureList =
                this.referenceSignatureService.filterListForUser(
                  referenceSignatureList,
                  this.userInfo
                );

              this.initSignatureSelectedSubscription();
            }
          );

        this.referenceCountriesService
          .getCountryListByZone()
          .subscribe((zoneList: ReferenceZoneModel[]) => {
            this.zoneList = zoneList;

            if (!this.userInfo?.isAdminAccount) {
              this.zoneList = this.zoneList
                .map((zone: ReferenceZoneModel) => {
                  zone.countryList = zone.countryList?.filter(
                    (country: ReferenceCountryModel) => {
                      return this.userInfo?.countryList.find(
                        (countryInList: ReferenceCountryModel) => {
                          return (
                            country.countryCode === countryInList.countryCode
                          );
                        }
                      );
                    }
                  );

                  return zone;
                })
                .filter(
                  (zone: ReferenceZoneModel) => zone.countryList?.length !== 0
                );
            }

            this.initCountrySelectedSubscription();
          });
      });

    this.userDashboardViewSelectedService.userDashboardViewSelectedSubject$.subscribe(
      (dashboardView?: DashboardViewEnum) => {
        this.currentDashboardViewSelected.setValue(
          dashboardView ?? DashboardViewEnum.DashboardViewInternationalCatalogs
        );
      }
    );
  }

  onSignatureSelectedChange(signatureInfoSelectedSelect: MatSelectChange) {
    const signatureInfoSelected = signatureInfoSelectedSelect.value;

    this.userSignatureInfoSelected = signatureInfoSelected;
    this.userSignatureSelectedService.updateUserSignatureSelected(
      signatureInfoSelected
    );
  }

  onCountrySelectedChange(countrySelected: MatSelectChange) {
    const countrySelectedId = countrySelected.value;

    const countrySelectedInList = this.countryList.find(
      (countryInList: ReferenceCountryModel) =>
        countryInList.countryCode === countrySelectedId
    );

    if (countrySelectedInList) {
      this.userCountrySelectedService.updateUserCountrySelected(
        countrySelectedInList.countryCode
      );
    }
  }

  initSignatureSelectedSubscription() {
    this.userSignatureSelectedService.userSignatureIdSelectedSubject$.subscribe(
      () => {
        const userSignatureSelectedObj =
          this.userSignatureSelectedService.getUserSignatureInfoSelected();

        if (userSignatureSelectedObj) {
          this.userSignatureInfoSelected =
            this.userSignatureSelectedService.getSignatureDataStringFromObj(
              userSignatureSelectedObj
            );

          // Get the signature id selected
          const signatureSelected = userSignatureSelectedObj.signatureId;

          // Get the signature object selected
          this.signatureSelected = this.referenceSignatureList.find(
            (referenceSignatureInList: ReferenceSignatureModel) =>
              referenceSignatureInList.id === signatureSelected
          );

          this.currentSignatureImg = this.getSignatureImg();
          this.currentSignatureImgSrcset = this.getSignatureImgSrcset();

          // Get the app service object selected
          if (userSignatureSelectedObj?.appServiceId) {
            this.appServiceSelected =
              this.referenceSignatureService.getAppServiceInSignatureList(
                this.referenceSignatureList,
                userSignatureSelectedObj.appServiceId
              );
          }
        }
      }
    );
  }

  initCountrySelectedSubscription() {
    this.userCountrySelectedService.userCountrySelectedSubject$.subscribe(
      (selectedCountryId?: string) => {
        if (selectedCountryId) {
          this.countryCodeSelected = selectedCountryId;

          this.countryList = [];
          this.zoneList.forEach((zoneInList: ReferenceZoneModel) => {
            this.countryList.push(...(zoneInList.countryList ?? []));
          });

          this.countrySelected = this.countryList.find(
            countryInList => countryInList.countryCode === selectedCountryId
          );
        }
      }
    );
  }

  redirectToUserManagementPage() {
    this._router.navigate(['/dashboard/user-management']).then(() => {});
  }

  getSignatureImg(): string | undefined {
    if (this.signatureSelected && this.signatureSelected.imgName) {
      return `assets/brand-logo/${this.signatureSelected.imgName}.png`;
    } else {
      return undefined;
    }
  }

  getSignatureImgSrcset(): string | undefined {
    if (this.signatureSelected && this.signatureSelected.imgName) {
      const imgBase = 'assets/brand-logo/' + this.signatureSelected.imgName;
      return `${imgBase}.png 1x,${imgBase}@2x.png 2x,${imgBase}@3x.png 3x`;
    } else {
      return undefined;
    }
  }

  isSignatureImgDisplayImgOnly(): boolean {
    if (this.signatureSelected) {
      return this.signatureSelected.displayImgOnly ?? false;
    } else {
      return false;
    }
  }

  onDashboardViewSelectedChange(event: MatButtonToggleChange) {
    const dashboardView = event.value as DashboardViewEnum;

    this.userDashboardViewSelectedService.updateUserDashboardViewSelected(
      dashboardView
    );

    if (
      dashboardView === DashboardViewEnum.DashboardViewInternationalCatalogs
    ) {
      this._router.navigateByUrl('/dashboard/products');
    }

    if (dashboardView === DashboardViewEnum.DashboardViewGlobal) {
      this._router.navigateByUrl('/dashboard/global');
    }
  }

  protected readonly DashboardViewEnum = DashboardViewEnum;
}
